interface User {
  id: string
  account: string
  name: string
  platform_role: 'owner' | 'admin' | 'auditor' | 'security' | 'user'
  type: 'local' | 'ldap' | 'oidc'
  created_at: string
  updated_at: string
  last_login_at?: string
  external_id?: string
  totp_enabled?: boolean
  contract_agreement?: boolean | string | null
}

interface LoginResponse {
  access_token: string
  refresh_token: string
  user: User
}

interface TOTPRequiredResponse {
  totp_required: true
  temp_token: string
}

interface LoginRequest {
  account: string
  password: string
}

interface TOTPVerifyLoginRequest {
  temp_token: string
  totp_code: string
}

interface RecoveryCodeLoginRequest {
  temp_token: string
  recovery_code: string
}

interface CreateOwnerRequest {
  account: string
  name: string
  password: string
}

interface CheckOwnerResponse {
  has_owner: boolean
}

interface UpdateProfileRequest {
  name: string
}

interface ChangePasswordRequest {
  old_password: string
  new_password: string
}

interface TOTPEnableResponse {
  secret: string
  qr_code_url: string
  recovery_codes: string[]
}

interface TOTPVerifyEnableRequest {
  recovery_codes: string[]
  secret: string
  totp_code: string
}

class AuthService {
  private readonly API_BASE = ""
  private readonly USER_KEY = 'synstream_user'
  private readonly ACCESS_TOKEN_KEY = 'synstream_access_token'
  private readonly REFRESH_TOKEN_KEY = 'synstream_refresh_token'

  // Storage methods
  setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user))
  }

  getUser(): User | null {
    const userData = localStorage.getItem(this.USER_KEY)
    if (!userData || userData === 'undefined' || userData === 'null') {
      return null
    }
    try {
      return JSON.parse(userData)
    } catch {
      return null
    }
  }

  setAccessToken(token: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, token)
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY)
  }

  setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token)
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY)
  }

  clearAuth(): void {
    localStorage.removeItem(this.USER_KEY)
    localStorage.removeItem(this.ACCESS_TOKEN_KEY)
    localStorage.removeItem(this.REFRESH_TOKEN_KEY)
  }

  isAuthenticated(): boolean {
    return !!this.getUser()
  }

  // API methods
  async login(credentials: LoginRequest): Promise<LoginResponse | TOTPRequiredResponse> {
    const response = await fetch(`${this.API_BASE}/api/v2/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
      credentials: 'include', // Important for cookie
    })

    if (!response.ok) {
      // Map HTTP status codes to specific error messages
      switch (response.status) {
        case 400:
          throw new Error('errors.badRequest')
        case 401:
          throw new Error('errors.wrongPassword')
        case 404:
          throw new Error('errors.accountNotFound')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.loginFailed')
      }
    }

    const data = await response.json()

    // Check if TOTP is required
    if ('totp_required' in data && data.totp_required) {
      return data as TOTPRequiredResponse
    }

    // Normal login success
    this.setUser(data.user)
    this.setAccessToken(data.access_token)
    this.setRefreshToken(data.refresh_token)
    return data as LoginResponse
  }

  async verifyTOTPLogin(request: TOTPVerifyLoginRequest): Promise<LoginResponse> {
    const response = await fetch(`${this.API_BASE}/api/v2/auth/totp-verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      credentials: 'include',
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
        case 401:
          throw new Error('errors.invalidTOTPCode')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.verifyTOTPFailed')
      }
    }

    const data: LoginResponse = await response.json()
    this.setUser(data.user)
    this.setAccessToken(data.access_token)
    this.setRefreshToken(data.refresh_token)
    return data
  }

  async verifyRecoveryCodeLogin(request: RecoveryCodeLoginRequest): Promise<LoginResponse> {
    const response = await fetch(`${this.API_BASE}/api/v2/auth/totp/recovery`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
      credentials: 'include',
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
        case 401:
          throw new Error('errors.invalidRecoveryCode')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.verifyRecoveryCodeFailed')
      }
    }

    const data: LoginResponse = await response.json()
    this.setUser(data.user)
    this.setAccessToken(data.access_token)
    this.setRefreshToken(data.refresh_token)
    return data
  }

  async logout(): Promise<void> {
    const refreshToken = this.getRefreshToken()
    const accessToken = this.getAccessToken()

    try {
      // Call logout API if we have refresh_token
      if (refreshToken) {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        }

        // Add Authorization header if we have access token
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`
        }

        await fetch(`${this.API_BASE}/api/v2/auth/logout`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ refresh_token: refreshToken }),
          credentials: 'include',
        })
      }
    } catch (error) {
      // Ignore logout errors, still clear local auth data
      console.error('Logout API error:', error)
    } finally {
      // Always clear auth data regardless of API call result
      this.clearAuth()
    }
  }

  async checkOwner(): Promise<boolean> {
    const response = await fetch(`${this.API_BASE}/api/v2/auth/check-owner`, {
      credentials: 'include',
    })

    if (!response.ok) {
      // Only handle 200 success and 500 server error
      switch (response.status) {
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.serverError')
      }
    }

    const data: CheckOwnerResponse = await response.json()
    return data.has_owner
  }

  async createOwner(data: CreateOwnerRequest): Promise<User> {
    const response = await fetch(`${this.API_BASE}/api/v2/auth/create-owner`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      credentials: 'include',
    })

    // Handle 201 success, 400 bad request, 500 server error
    if (response.status === 201) {
      const result = await response.json()
      return result.user
    }

    // Handle errors
    switch (response.status) {
      case 400:
        throw new Error('errors.badRequest')
      case 500:
        throw new Error('errors.serverError')
      default:
        throw new Error('errors.createOwnerFailed')
    }
  }

  async createOwnerAndLogin(data: CreateOwnerRequest): Promise<LoginResponse | TOTPRequiredResponse> {
    await this.createOwner(data)
    return this.login({ account: data.account, password: data.password })
  }

  async refreshToken(): Promise<User> {
    const refreshToken = this.getRefreshToken()

    if (!refreshToken) {
      this.clearAuth()
      throw new Error('errors.sessionExpired')
    }

    const response = await fetch(`${this.API_BASE}/api/v2/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
      credentials: 'include',
    })

    if (!response.ok) {
      // If refresh fails, clear auth and throw error
      this.clearAuth()
      switch (response.status) {
        case 401:
          throw new Error('errors.sessionExpired')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.refreshFailed')
      }
    }

    const data: LoginResponse = await response.json()
    this.setUser(data.user)
    this.setAccessToken(data.access_token)
    this.setRefreshToken(data.refresh_token)
    return data.user
  }

  async updateProfile(data: UpdateProfileRequest): Promise<User> {
    const response = await fetch(`${this.API_BASE}/api/v2/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
          throw new Error('errors.badRequest')
        case 401:
          throw new Error('errors.unauthorized')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.updateFailed')
      }
    }

    const partialUpdate = await response.json()
    const currentUser = this.getUser()

    if (!currentUser) {
      throw new Error('errors.unauthorized')
    }

    // Merge partial update with current user data
    const updatedUser: User = { ...currentUser, ...partialUpdate }
    this.setUser(updatedUser)
    return updatedUser
  }

  async changePassword(data: ChangePasswordRequest): Promise<void> {
    const accessToken = this.getAccessToken()

    const response = await fetch(`${this.API_BASE}/api/v2/profile/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
          throw new Error('errors.badRequest')
        case 401:
          throw new Error('errors.wrongPassword')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.changePasswordFailed')
      }
    }
  }

  async getProfile(): Promise<User> {
    const response = await fetch(`${this.API_BASE}/api/v2/profile`, {
      method: 'GET',
      credentials: 'include',
    })

    if (!response.ok) {
      switch (response.status) {
        case 401:
          throw new Error('errors.unauthorized')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.getProfileFailed')
      }
    }

    const user: User = await response.json()
    this.setUser(user)
    return user
  }

  async agreeContract(): Promise<User> {
    const response = await fetch(`${this.API_BASE}/api/v2/profile/contract`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ agree: true }),
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
          throw new Error('errors.badRequest')
        case 401:
          throw new Error('errors.unauthorized')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.agreeContractFailed')
      }
    }

    // After agreeing, fetch the latest profile to get updated state
    return this.getProfile()
  }

  async enableTOTP(): Promise<TOTPEnableResponse> {
    const response = await fetch(`${this.API_BASE}/api/v2/profile/totp/enable`, {
      method: 'POST',
      credentials: 'include',
    })

    if (!response.ok) {
      switch (response.status) {
        case 401:
          throw new Error('errors.unauthorized')
        case 409:
          throw new Error('errors.totpAlreadyEnabled')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.enableTOTPFailed')
      }
    }

    return response.json()
  }

  async verifyEnableTOTP(data: TOTPVerifyEnableRequest): Promise<void> {
    const response = await fetch(`${this.API_BASE}/api/v2/profile/totp/verify-enable`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
          throw new Error('errors.invalidTOTPCode')
        case 401:
          throw new Error('errors.unauthorized')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.verifyTOTPFailed')
      }
    }

    // After enabling TOTP, refresh user profile
    await this.getProfile()
  }

  async disableTOTP(password: string): Promise<void> {
    const response = await fetch(`${this.API_BASE}/api/v2/profile/totp`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ password }),
    })

    if (!response.ok) {
      switch (response.status) {
        case 400:
          throw new Error('errors.badRequest')
        case 401:
          throw new Error('errors.wrongPassword')
        case 500:
          throw new Error('errors.serverError')
        default:
          throw new Error('errors.disableTOTPFailed')
      }
    }

    // After disabling TOTP, refresh user profile
    await this.getProfile()
  }
}

export const authService = new AuthService()
export type { User, LoginResponse, TOTPRequiredResponse, LoginRequest, TOTPVerifyLoginRequest, RecoveryCodeLoginRequest, CreateOwnerRequest, UpdateProfileRequest, ChangePasswordRequest, TOTPEnableResponse, TOTPVerifyEnableRequest }
